import { FC, memo, useCallback, useState } from 'react'

import { FolderOutlined } from '@mui/icons-material'
import { Button, CircularProgress, Collapse, Stack, Typography, useTheme } from '@mui/material'

import Icon from 'src/@core/components/icon'

import { useMentorCloudFolders } from 'src/hooks/files/useMentorCloudFolders'

import { Folder } from 'src/types/files/file'

interface MentorCloudFolderProps {
  folder: Folder
  isActive: boolean
  onClick?: (folder?: Folder | null) => void
  onClickSubFolder?: (folder?: Folder | null) => void
}

export const MentorCloudFolder: FC<MentorCloudFolderProps> = memo((props: MentorCloudFolderProps) => {
  const { folder, isActive = false, onClick, onClickSubFolder } = props

  const theme = useTheme()

  const { data: folders, isFetching } = useMentorCloudFolders(folder.id, isActive)
  const [activeFolder, setActiveFolder] = useState<Folder | null | undefined>(null)

  const onClickFolder = (subFolder?: Folder | null) => {
    if (activeFolder?.id === subFolder?.id) {
      setActiveFolder(null)
      onClickSubFolder?.(folder)

      return
    }

    onClickSubFolder?.(subFolder)
    setActiveFolder(subFolder)
  }

  const onFolderClick = useCallback(async () => {
    onClick?.(folder)
  }, [onClick, folder])

  return (
    <Stack width={'100%'}>
      <Button
        variant={isActive ? 'contained' : 'tonal'}
        onClick={onFolderClick}
        startIcon={<FolderOutlined />}
        sx={{ justifyContent: 'start' }}
        disabled={isFetching}
        fullWidth
      >
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          width={'100%'}
          alignItems={'center'}
          overflow={'hidden'}
        >
          <Typography
            textOverflow={'ellipsis'}
            color={'inherit'}
            fontSize={'inherit'}
            style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
          >
            {folder?.name}
          </Typography>
          {isFetching ? (
            <CircularProgress size={theme.spacing(4)} />
          ) : (
            <Stack alignItems={'center'} justifyContent={'center'}>
              <Icon
                fontSize={theme.spacing(5)}
                style={{ justifySelf: 'end' }}
                icon={isActive ? 'tabler:chevron-up' : 'tabler:chevron-down'}
              />
            </Stack>
          )}
        </Stack>
      </Button>
      <Collapse orientation='vertical' in={isActive}>
        {folders && folders?.length > 0 && (
          <Stack paddingLeft={theme.spacing(2)} paddingTop={theme.spacing(2)} gap={theme.spacing(2)}>
            {folders?.map(subFolder => (
              <MentorCloudFolder
                key={subFolder.id}
                isActive={activeFolder?.id === subFolder.id}
                onClick={onClickFolder}
                onClickSubFolder={onClickSubFolder}
                folder={subFolder}
              />
            ))}
          </Stack>
        )}
      </Collapse>
    </Stack>
  )
})
